'use client'

import { useState, useEffect, useCallback } from 'react';
import styles from '../styles/VerifyEmail.module.css';
import { useAccount } from 'wagmi';
import { useAuth } from '../../context/AuthContext';

interface EmailVerificationFormProps {
  onPrevious: () => void;
  onNext: () => void;
}

export const EmailVerificationForm = ({ onPrevious, onNext }: EmailVerificationFormProps) => {
  const [walletAddress, setWalletAddress] = useState<string | null>(null);
  const [emailAddress, setEmailAddress] = useState<string | null>(null);
  const [verifiedEmail, setVerifiedEmail] = useState(false);
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationStatus, setVerificationStatus] = useState<'idle' | 'sending' | 'sent' | 'verifying' | 'verified' | 'error'>('idle');
  const [verificationError, setVerificationError] = useState<string | null>(null);
  const [localStatus, setLocalStatus] = useState<'idle' | 'checking'>('idle');
  const [isNewUser, setIsNewUser] = useState(false);
  
  // Use wagmi's useAccount hook to get the wallet address
  const { address: wagmiAddress } = useAccount();
  const { user, checkWalletAuth } = useAuth();
  
  // Get wallet address and check for stored email verification
  useEffect(() => {
    const getWalletInfo = async () => {
      try {
        // Get address from wagmi first (most reliable)
        if (wagmiAddress) {
          console.log('EmailVerificationForm: Using wagmi address:', wagmiAddress);
          setWalletAddress(wagmiAddress);
        } 
        // Fallback to AppKit
        else if (window.AppKit?.Web3Modal?.getAddress) {
          const address = await window.AppKit.Web3Modal.getAddress();
          console.log('EmailVerificationForm: Using AppKit getAddress():', address);
          if (address) {
            setWalletAddress(address);
          }
        } else if (window.AppKit?.Web3Modal?.state?.address) {
          const address = window.AppKit.Web3Modal.state.address;
          console.log('EmailVerificationForm: Using AppKit state address:', address);
          setWalletAddress(address);
        }
        
        // First check if the user is already verified in auth context
        if (user && user.verifiedEmail) {
          console.log('EmailVerificationForm: User already verified from auth context');
          setEmailAddress(user.email);
          setVerifiedEmail(true);
          setVerificationStatus('verified');
          setEmail(user.email);
          return;
        }
        
        // Fallback to localStorage
        const storedEmail = localStorage.getItem('gam_verified_email');
        const storedVerified = localStorage.getItem('gam_email_verified');
        
        if (storedEmail && storedVerified === 'true') {
          console.log('EmailVerificationForm: Found stored verified email:', storedEmail);
          setEmailAddress(storedEmail);
          setVerifiedEmail(true);
          setVerificationStatus('verified');
          // Pre-fill the email field
          setEmail(storedEmail);
        }
      } catch (err) {
        console.error('Error getting wallet address:', err);
      }
    };
    
    getWalletInfo();
  }, [wagmiAddress, user]);

  // Check email verification status
  const checkVerificationStatus = useCallback(async (): Promise<boolean> => {
    if (!walletAddress || !emailAddress) return false;
    
    try {
      const response = await fetch(`/api/presale/verify-email/status?walletAddress=${walletAddress}&email=${encodeURIComponent(emailAddress)}`);
      const data = await response.json();
      
      if (data.success && data.verified) {
        setVerifiedEmail(true);
        setVerificationStatus('verified');
        
        // Store in localStorage using wallet address as key
        localStorage.setItem(`gam_verified_email_${walletAddress}`, emailAddress);
        localStorage.setItem(`gam_email_verified_${walletAddress}`, 'true');
        
        // Refresh auth context to get the user data
        await checkWalletAuth();
        
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error checking verification status:', error);
      return false;
    }
  }, [walletAddress, emailAddress, checkWalletAuth]);

  // Submit email for verification
  const submitEmail = async (email: string): Promise<boolean> => {
    if (!walletAddress) return false;
    
    try {
      setVerificationStatus('sending');
      setVerificationError(null);
      
      const response = await fetch('/api/presale/verify-email/initiate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          walletAddress,
        }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        setEmailAddress(email);
        setVerificationStatus('sent');
        return true;
      } else {
        setVerificationError(data.message || 'Failed to send verification code');
        setVerificationStatus('error');
        return false;
      }
    } catch (error) {
      console.error('Error sending verification email:', error);
      setVerificationError('Failed to send verification code');
      setVerificationStatus('error');
      return false;
    }
  };
  
  // Verify the code entered by the user
  const verifyCode = async (code: string): Promise<boolean> => {
    if (!walletAddress || !emailAddress) return false;
    
    try {
      setVerificationStatus('verifying');
      setVerificationError(null);
      
      const response = await fetch('/api/presale/verify-email/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: emailAddress,
          walletAddress,
          code,
        }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        setVerifiedEmail(true);
        setVerificationStatus('verified');
        setIsNewUser(data.isNewUser);
        
        // Store in localStorage using wallet address as key
        localStorage.setItem(`gam_verified_email_${walletAddress}`, emailAddress);
        localStorage.setItem(`gam_email_verified_${walletAddress}`, 'true');
        
        // Refresh auth context to get the user data
        await checkWalletAuth();
        
        return true;
      } else {
        setVerificationError(data.message || 'Invalid verification code');
        setVerificationStatus('error');
        return false;
      }
    } catch (error) {
      console.error('Error verifying code:', error);
      setVerificationError('Failed to verify code');
      setVerificationStatus('error');
      return false;
    }
  };
  
  const handleSubmitEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('handleSubmitEmail called. Wallet Address:', walletAddress, 'Email:', email);
    if (!email || !walletAddress) {
      console.error('Email or Wallet Address missing!');
      setVerificationError('Wallet address not connected or email missing.');
      setVerificationStatus('error');
      return;
    }
    await submitEmail(email);
  };
  
  const handleSubmitCode = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!verificationCode) return;
    
    const success = await verifyCode(verificationCode);
    if (success) {
      // Wait a bit before proceeding to the next step to show the success state
      setTimeout(() => {
        onNext();
      }, 1500);
    }
  };
  
  const handleResendCode = async () => {
    if (!emailAddress) return;
    
    await submitEmail(emailAddress);
    setVerificationCode(''); // Clear the code field
  };
  
  // For demo purposes only - allows skipping email verification
  const handleSkipVerification = () => {
    if (process.env.NODE_ENV === 'development') {
      setVerifiedEmail(true);
      // Also use address-specific key for skipping in dev
      if (walletAddress) {
        localStorage.setItem(`gam_verified_email_${walletAddress}`, email || 'demo@example.com');
        localStorage.setItem(`gam_email_verified_${walletAddress}`, 'true');
      } else {
        // Fallback for dev if address is not available (less ideal)
        localStorage.setItem('gam_verified_email', email || 'demo@example.com');
        localStorage.setItem('gam_email_verified', 'true');
      }
      onNext();
    }
  };

  // Already verified, show a success message and allow proceeding
  if (verifiedEmail) {
    return (
      <div className={styles.emailStep}>
        <button onClick={onPrevious} className={styles.backButton}>
          ← Back
        </button>
        
        <div className={styles.emailVerified}>
          <div className={styles.icon}>✓</div>
          <h3>Email Verified</h3>
          <p>Your email address <strong>{emailAddress}</strong> has been verified.</p>
          
          <button 
            className={styles.button} 
            onClick={onNext}
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  // Code verification screen
  if (verificationStatus === 'sent' || verificationStatus === 'verifying') {
    return (
      <div className={styles.emailStep}>
        <button onClick={onPrevious} className={styles.backButton}>
          ← Back
        </button>
        
        <h3>Enter Verification Code</h3>
        <p>We have sent a verification code to <strong>{emailAddress}</strong>. Please check your inbox (and spam folder) and enter the 6-digit code below.</p>
        
        <form onSubmit={handleSubmitCode}>
          <div className={styles.formGroup}>
            <label htmlFor="verificationCode">Verification Code</label>
            <input
              type="text"
              id="verificationCode"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').substring(0, 6))}
              placeholder="123456"
              maxLength={6}
              required
              pattern="[0-9]{6}"
              inputMode="numeric"
              autoComplete="one-time-code"
            />
          </div>
          
          {verificationError && (
            <div className={styles.error}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              {verificationError}
            </div>
          )}
          
          <button 
            type="submit" 
            className={styles.button}
            disabled={verificationCode.length !== 6 || verificationStatus === 'verifying'}
          >
            {verificationStatus === 'verifying' && <span className={styles.spinner}></span>}
            {verificationStatus === 'verifying' ? 'Verifying...' : 'Verify Code'}
          </button>
          
          <div className={styles.resendContainer}>
            <button 
              type="button"
              onClick={handleResendCode}
              className={styles.resendButton}
              disabled={verificationStatus === 'verifying'}
            >
              Resend Code
            </button>
          </div>
          
          {process.env.NODE_ENV === 'development' && (
            <button 
              type="button"
              className={`${styles.button} ${styles.secondary}`}
              onClick={handleSkipVerification}
            >
              Skip Verification (Dev Only)
            </button>
          )}
        </form>
      </div>
    );
  }

  // Email input form (initial state)
  return (
    <div className={styles.emailStep}>
      <button onClick={onPrevious} className={styles.backButton}>
        ← Back
      </button>
      
      <h3>Verify Your Email</h3>
      <p>Please enter your email address to continue with the purchase.</p>
      
      <form onSubmit={handleSubmitEmail}>
        <div className={styles.formGroup}>
          <label htmlFor="email">Email Address</label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            required
          />
        </div>
        
        {verificationError && (
          <div className={styles.error}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            {verificationError}
          </div>
        )}
        
        <button 
          type="submit" 
          className={styles.button}
          disabled={!email || verificationStatus === 'sending'}
        >
          {verificationStatus === 'sending' && <span className={styles.spinner}></span>}
          {verificationStatus === 'sending' ? 'Sending...' : 'Send Verification Code'}
        </button>
      </form>
    </div>
  );
}; 